import React, { useCallback, useState } from 'react';
import { ActivityIndicator, View } from 'react-native';
import { createStyleSheet, UnistylesRuntime, useStyles } from 'react-native-unistyles';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowLeft, faCheckCircle, faCircleInfo, faUserGroup, IconDefinition } from '@fortawesome/free-solid-svg-icons';
import { faCalendar, faClock } from '@fortawesome/pro-regular-svg-icons';
import MyAppText from '../../../../components/MyAppText';
import { translate } from '../../../../services/translate';
import { Tabs } from '../../../../components/Tabs';
import CustomButton from '../../../../components/CustomButton';
import { ScrollView } from 'react-native-web-hover';
import moment from 'moment';
import Badge from '../../../../components/Badge';
import { DEFAULT_PASTEL_COLORS, DEFAULT_PASTEL_TEXT_COLORS } from '../../../../styles/colorPalettes';
import Tooltip from '../../../../components/Tooltip';
import { useFocusEffect, useIsFocused } from '@react-navigation/native';
import { appAgentService, InvalidPermissionToVacancy } from '../../../../services/central-api/app-agent';
import Toast from 'react-native-toast-message';
import ApplicationCancelModal from './ApplicationCancelModal';
import { RouteProp, useRoute } from '@react-navigation/native';
import { AppAgentParamList } from '../../../../typings/Params';
import { RootNavigation } from '../../../../Routes';

export default function VacancyDetailsCancel({ navigation }: {
    navigation: RootNavigation;
}) {
    const { styles, theme } = useStyles(styleSheet);
    const route = useRoute<RouteProp<AppAgentParamList, 'VacancyDetailsCancel'>>();

    const [isConfirmModalOpen, setIsConfirmModalOpen] = useState<boolean>(false);
    const [application, setApplication] = useState<Application | undefined>(undefined);
    const [isLoading, setIsLoading] = useState<boolean>(false);

    async function getApplication() {
        try {
            setIsLoading(true);
            const response = await appAgentService.getApplication(route.params.id);
            setApplication(response);
        } catch (err) {
            if (err instanceof InvalidPermissionToVacancy) {
                return Toast.show({
                    type: 'sentinelxError',
                    text1: translate('invalidPermissionToVacancy'),
                });
            }
            console.error(err);
            Toast.show({
                type: 'sentinelxError',
                text1: translate('unexpectedError'),
            });
        } finally {
            setIsLoading(false);
        }
    }

    const isFocused = useIsFocused();
    useFocusEffect(useCallback(() => {
        if (isFocused) {
            getApplication();
        }
    }, [isFocused, navigation, route.params]));

    function goBack() {
        navigation.canGoBack() ? navigation.goBack() : navigation.navigate('AppAgent', { screen: 'Candidacies' });
    }

    function allowCancellation() {
        if (application?.vacancy.applicationsFinish) {
            return moment().isBefore(moment(application?.vacancy.applicationsFinish));
        } else {
            return moment().isBefore(moment(application?.vacancy.activityStart));
        }
    }

    return (
        <View style={styles.container(UnistylesRuntime.themeName == 'dark')}>
            <View style={{ flexDirection: 'row', alignItems: 'flex-start' }}>
                <FontAwesomeIcon
                    icon={faArrowLeft}
                    color={theme.colors.iconColor}
                    fontSize={20}
                    cursor='pointer'
                    onClick={() => goBack()}
                />
            </View>
            <View style={styles.content}>
                {isLoading
                    ? <View style={{ flex: 1, width: '100%', alignItems: 'center', justifyContent: 'center', }}>
                        <ActivityIndicator size='large' color={theme.colors.loadingColor} />
                    </View>
                    : !application
                        ? <View style={{ flex: 1, width: '100%', alignItems: 'center', justifyContent: 'center', }}>
                            <MyAppText style={[styles.vacanciesText, { textAlign: 'center' }]}>{translate('invalidPermissionToVacancy')}</MyAppText>
                        </View>
                        : <>
                            <View style={styles.contentHeader}>
                                <View style={{ flexDirection: 'column', gap: 8, alignItems: 'center' }}>
                                    <MyAppText style={styles.activityTitle}>{application.vacancy.title}</MyAppText>
                                    <View style={{ flexDirection: 'row', columnGap: 2 }}>
                                        <FontAwesomeIcon icon={faUserGroup} fontSize={17} color={theme.colors.neutral['400']} />
                                        <MyAppText style={styles.vacanciesText}>{application.vacancy.quantity} {translate('vacancies').toLowerCase()}</MyAppText>
                                    </View>

                                </View>
                            </View>
                            <Tabs
                                tabs={[{
                                    key: 'vacancyInfo',
                                    label: translate('vacancyInfo'),
                                }, {
                                    key: 'requirements',
                                    label: translate('requirements'),
                                }]}
                            >
                                {({ selectedTab }) => {
                                    if (selectedTab == 'vacancyInfo') {
                                        return <View style={styles.vacancyInfo}>
                                            <ScrollView style={{ flex: 1 }} contentContainerStyle={{ gap: 16 }}>
                                                <View style={styles.vacancyInfoItem}>
                                                    <MyAppText style={styles.vacancyInfoItemTitle}>{translate('workplace')}</MyAppText>
                                                    <MyAppText style={styles.vacancyInfoItemDescription}>{application.vacancy.workplace.name}</MyAppText>
                                                </View>
                                                <View style={styles.vacancyInfoItem}>
                                                    <MyAppText style={styles.vacancyInfoItemTitle}>{translate('unit')}</MyAppText>
                                                    <MyAppText style={styles.vacancyInfoItemDescription}>{application.vacancy.workplace.actingBodyUnit.name}</MyAppText>
                                                </View>
                                                <View style={styles.vacancyInfoItem}>
                                                    <MyAppText style={styles.vacancyInfoItemTitle}>{translate('place')}</MyAppText>
                                                    <MyAppText style={styles.vacancyInfoItemDescription}>{application.vacancy.workplace.address}</MyAppText>
                                                </View>
                                                <View style={styles.vacancyInfoItem}>
                                                    <MyAppText style={styles.vacancyInfoItemTitle}>{translate('activityDescription')}</MyAppText>
                                                    <MyAppText style={styles.vacancyInfoItemDescription}>{application.vacancy.description}</MyAppText>
                                                </View>
                                                <View style={styles.vacancyInfoItem}>
                                                    <MyAppText style={styles.vacancyInfoItemTitle}>{translate('activityDate')}</MyAppText>
                                                    <View style={styles.iconWithText}>
                                                        <FontAwesomeIcon
                                                            icon={faCalendar as IconDefinition}
                                                            color={theme.colors.neutral['400']}
                                                            fontSize={16}
                                                            cursor='pointer'
                                                        />
                                                        <MyAppText style={styles.vacancyInfoItemDescription}>{moment(application.vacancy.activityStart).format('DD/MM')}</MyAppText>
                                                    </View>
                                                </View>
                                                <View style={styles.vacancyInfoItem}>
                                                    <MyAppText style={styles.vacancyInfoItemTitle}>{translate('activityHours')}</MyAppText>
                                                    <View style={styles.iconWithText}>
                                                        <FontAwesomeIcon
                                                            icon={faClock as IconDefinition}
                                                            color={theme.colors.neutral['400']}
                                                            fontSize={16}
                                                            cursor='pointer'
                                                        />

                                                        <MyAppText style={styles.vacancyInfoItemDescription}>{moment(application.vacancy.activityStart).format('HH:mm')} - {moment(application.vacancy.activityFinish).format('HH:mm')}</MyAppText>
                                                    </View>
                                                </View>
                                                <View style={styles.vacancyInfoItem}>
                                                    <MyAppText style={styles.vacancyInfoItemTitle}>{translate('levelAndTags')}</MyAppText>
                                                    <View style={{ flexDirection: 'column', gap: 16 }}>
                                                        <View style={{ flexDirection: 'column' }}>
                                                            <MyAppText style={styles.vacancyInfoItemSubTitle}>{translate('level')}:</MyAppText>
                                                            <MyAppText style={styles.vacancyInfoItemDescription}>{application.vacancy.levels.map(l => l.name).join(', ')}</MyAppText>
                                                        </View>
                                                        <View style={{ flexDirection: 'column' }}>
                                                            <MyAppText style={styles.vacancyInfoItemSubTitle}>{translate('tags')}:</MyAppText>
                                                            <ScrollView style={{ maxHeight: 80 }} contentContainerStyle={{ flexDirection: 'row', columnGap: 8, rowGap: 4, flexWrap: 'wrap' }}>
                                                                {application.vacancy.tags?.map((item) => {
                                                                    const index = DEFAULT_PASTEL_COLORS.indexOf(item.color);
                                                                    return <Badge
                                                                        mode='ROUNDED'
                                                                        key={item.id}
                                                                        backgroundColor={item.color}
                                                                        textColor={DEFAULT_PASTEL_TEXT_COLORS[index]}
                                                                    >
                                                                        {item.name}
                                                                    </Badge>;
                                                                })}
                                                            </ScrollView>
                                                        </View>

                                                    </View>
                                                </View>
                                                <View style={styles.vacancyInfoItem}>
                                                    <View style={{ zIndex: 2, flexDirection: 'row', columnGap: 8 }}>
                                                        <MyAppText style={styles.vacancyInfoItemTitle}>{translate('applicationCancellation')}</MyAppText>
                                                        <Tooltip
                                                            useSimpleBox
                                                            style={{
                                                                color: theme.colors.white['500'],
                                                                padding: 16,
                                                                borderRadius: 12,
                                                                width: 150,
                                                                justifyContent: 'center',
                                                                zIndex: 9999,
                                                                background: theme.colors.neutral['500'],
                                                            }}
                                                            tooltipContent={translate('applicationCancellationHintMessage')}
                                                            tooltipDirection='left'
                                                        >
                                                            <FontAwesomeIcon icon={faCircleInfo} fontSize={14} color={theme.colors.neutral['500']} />
                                                        </Tooltip>
                                                    </View>
                                                    <MyAppText
                                                        style={styles.vacancyInfoItemDescriptionDanger}
                                                    >
                                                        {translate('cancellationAvailableUntil')} {application.vacancy.applicationsFinish ? moment(application.vacancy.applicationsFinish).format('DD/MM/YYYY HH:mm') : moment(application.vacancy.activityStart).format('DD/MM/YYYY HH:mm')}
                                                    </MyAppText>
                                                </View>
                                            </ScrollView>
                                        </View>;
                                    } else if (selectedTab == 'requirements') {
                                        return <View style={styles.requirements}>
                                            <MyAppText style={styles.activityRequirementsText}>{translate('activityRequirements')}</MyAppText>
                                            <ScrollView style={{ flex: 1 }} contentContainerStyle={{ gap: 8 }}>
                                                {application.vacancy.skills.map(skill =>
                                                    <View key={skill.id} style={styles.iconWithText}>
                                                        <FontAwesomeIcon
                                                            icon={faCheckCircle}
                                                            color={theme.colors.successes['300']}
                                                            fontSize={20}
                                                            cursor='pointer'
                                                            onClick={() => { }}
                                                        />
                                                        <MyAppText style={styles.requirementText}>{skill.name}</MyAppText>

                                                    </View>
                                                )}
                                            </ScrollView>
                                        </View>;
                                    }
                                }}
                            </Tabs>
                        </>}
                {allowCancellation() &&
                    <View>
                        <CustomButton
                            text={translate('cancelApplication')}
                            onPress={() => setIsConfirmModalOpen(true)}
                            type='primary'
                            disabled={isLoading || !application}
                        />
                    </View>
                }
            </View>
            {application && <ApplicationCancelModal
                isModalVisible={isConfirmModalOpen}
                setModalVisible={setIsConfirmModalOpen}
                application={application}
                onConfirm={() => navigation.navigate('AppAgent', { screen: 'Candidacies' })}
            />}
        </View>
    );
}

const styleSheet = createStyleSheet((theme) => ({
    container: (isDark: boolean) => ({
        flexDirection: 'column',
        justifyContent: 'center',
        backgroundColor: isDark ? theme.colors.paginationBar.background : theme.colors.backgrounds['BG-2'],
        flex: 1,
        padding: 16,
        gap: 8,
    }),
    content: {
        flex: 1,
        height: 'auto',
        gap: 16,
    },
    contentHeader: {
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'center',
        gap: 16,
    },
    activityTitle: {
        fontSize: 18,
        fontWeight: '700',
        color: theme.colors.neutral['500']
    },
    vacanciesText: {
        fontSize: 14,
        fontWeight: '500',
        color: theme.colors.neutral['400']
    },
    requirements: {
        flex: 1,
        flexDirection: 'column',
        gap: 8,
    },
    activityRequirementsText: {
        fontSize: 16,
        fontWeight: '700',
        color: theme.colors.primary['500']
    },
    requirementText: {
        fontSize: 14,
        fontWeight: '500',
        color: theme.colors.neutral['400']
    },
    vacancyInfo: {
        flex: 1,
        flexDirection: 'column',
    },
    vacancyInfoItem: {
        flexDirection: 'column',
        gap: 4,
    },
    vacancyInfoItemTitle: {
        fontSize: 16,
        fontWeight: '700',
        color: theme.colors.primary['500']
    },
    vacancyInfoItemSubTitle: {
        fontSize: 14,
        fontWeight: '700',
        color: theme.colors.neutral['500']
    },
    vacancyInfoItemDescription: {
        fontSize: 14,
        fontWeight: '500',
        color: theme.colors.neutral['400']
    },
    vacancyInfoItemDescriptionDanger: {
        fontSize: 14,
        fontWeight: '500',
        color: theme.colors.errors['300']
    },
    iconWithText: {
        flexDirection: 'row',
        gap: 8,
        alignItems: 'center'
    }
}));
